// Final Checker — Business Rule (BR) verification page.
//
// Mirrors ocr-ui/src/modules/document-extraction/tracking/verification/
// index.jsx, which is the simpler counterpart to ToolsView. Same
// endpoints as the cockpit, but:
//   - verification_component: "BR" (not "TV")
//   - projection collection: "br_lineitem_content" / "br_fielditem_content"
//
// The page is intentionally slim — no Tools View configuration, no
// manual adjustment, no rule editor. Reviewers see the extracted
// values, optionally toggle a JSON view, then Reset / Save / Clear /
// Reject / Submit.

import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Save,
  RotateCcw,
  Eraser,
  XCircle,
  Send,
  Code2,
  Download,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Minimize2,
} from "lucide-react";

import { ocrApi } from "../../services/ocrApi";
import { DocumentImageWithHighlight } from "../components/documents/DocumentImageWithHighlight";
import { FieldConnectingLine } from "../components/documents/FieldConnectingLine";
import { FieldItemPanel } from "../components/documents/FieldItemPanel";
import { LineItemPanel } from "../components/documents/LineItemPanel";
import { ResponseJsonViewer } from "../components/documents/ResponseJsonViewer";
import { useResolveOcrAccount } from "../hooks/useResolveOcrAccount";
import { useDocumentPan } from "../hooks/useDocumentPan";

type TabKey = "fielditem" | "lineitem";

const VERIFICATION_COMPONENT = "BR";
const DEFAULT_TABLE_NAME = "doc_extraction_tracking";

// Collection names the BR step writes to (vs `lineitem_content` /
// `fielditem_content` used by TV). Sourced from ocr-ui's
// EXTRACTION_VIEWER_TABS.brKeyName.
const BR_LINE_COLLECTION = "br_lineitem_content";
const BR_FIELD_COLLECTION = "br_fielditem_content";

interface PageData {
  image_path?: string;
  image_size?: string;
  extracted_content?: unknown;
  [key: string]: unknown;
}

export default function FinalCheckerPage() {
  const { xUserId: resolvedXUserId } = useResolveOcrAccount();
  const ocrReady = resolvedXUserId != null;

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const processId = searchParams.get("id") || "";
  const workflowId = searchParams.get("workflow_id") || "";
  const uploader = searchParams.get("uploader") || "";
  const filenameParam = searchParams.get("filename") || "";
  // `table_name` is forwarded by the cockpit / detail panel so the
  // Final Checker can roundtrip back to the right inbox (Template Engine
  // vs OCR Tracking) and query the right backend table.
  const tableName = searchParams.get("table_name") || DEFAULT_TABLE_NAME;
  const isConfigStudio = tableName === "config_studio_tracking";
  const inboxPath = isConfigStudio ? "/studio/templateEngine" : "/ocr-tracking";

  const requestReady =
    !!processId && !!workflowId && !!uploader && ocrReady;

  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState<TabKey>("lineitem");
  const [goToInput, setGoToInput] = useState("");
  const [showJson, setShowJson] = useState(false);
  const [activeBoundary, setActiveBoundary] = useState<string | null>(null);

  /**
   * Document zoom level (percent). 100 = fit-to-pane. Range clamped to
   * 50–200 to match what `DocumentImageWithHighlight` accepts and to
   * keep the resize observers / overlay math stable. Reset to 100 when
   * the page navigates to a different document so a stale "zoomed in"
   * state from the previous doc doesn't carry over.
   */
  const [zoom, setZoom] = useState<number>(100);
  const ZOOM_MIN = 50;
  const ZOOM_MAX = 200;
  const ZOOM_STEP = 10;
  // Reset zoom on doc change.
  useEffect(() => {
    setZoom(100);
  }, [filenameParam]);

  /**
   * Fullscreen toggle for the document pane.
   *
   * "Faux" fullscreen — we position the document section absolutely at
   * `inset-0 z-50` rather than using the browser's Fullscreen API. The
   * faux approach keeps React state, all overlay SVGs, the right-side
   * data panel actions (Save / Reject etc.), and toasts reachable
   * without requiring user gestures for the Fullscreen API. Esc exits.
   * Body scroll is locked while active so the rest of the page doesn't
   * scroll behind the overlay.
   */
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Esc key exits fullscreen.
  useEffect(() => {
    if (!isFullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsFullscreen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isFullscreen]);

  // Lock body scroll while fullscreen so the rest of the page doesn't
  // bleed through if the document is shorter than the viewport.
  useEffect(() => {
    if (!isFullscreen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isFullscreen]);

  // Exit fullscreen on doc navigation so the next doc opens in normal
  // layout instead of taking over the screen unexpectedly.
  useEffect(() => {
    setIsFullscreen(false);
  }, [filenameParam, currentPage]);

  /**
   * Click-and-drag pan for the document pane. The hook attaches a
   * ref + mousedown handler to the scroll container; we forward
   * `panHandlers` and use `isPanning` to swap cursor between grab /
   * grabbing. Interactive descendants (bbox, table grid, OCR words,
   * field-rule overlays) are gated by `data-interaction="true"` inside
   * the hook so their own drag handlers still win.
   *
   * Final Checker has no drawing modes, so we never pass `disabled`.
   */
  const docPan = useDocumentPan<HTMLElement>();

  const collectionName =
    activeTab === "fielditem" ? BR_FIELD_COLLECTION : BR_LINE_COLLECTION;

  // ----- Queries -------------------------------------------------------

  const homeQuery = useQuery({
    queryKey: ["ocr", "br-verification", "home", processId],
    queryFn: () =>
      ocrApi.verificationHome({
        processId,
        workflowId,
        uploader,
        verificationComponent: VERIFICATION_COMPONENT,
        tableName,
      }),
    enabled: requestReady,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const homePayload = (homeQuery.data ?? null) as Record<string, unknown> | null;

  const pages = useMemo<Record<string, PageData> | null>(() => {
    if (!homePayload) return null;
    const direct = homePayload.pages;
    if (direct && typeof direct === "object" && !Array.isArray(direct)) {
      return direct as Record<string, PageData>;
    }
    const wrapped = (homePayload.data as Record<string, unknown> | undefined)
      ?.pages;
    if (wrapped && typeof wrapped === "object" && !Array.isArray(wrapped)) {
      return wrapped as Record<string, PageData>;
    }
    return null;
  }, [homePayload]);

  const pageData: PageData | null = pages?.[String(currentPage)] ?? null;

  const projectionQuery = useQuery({
    queryKey: [
      "ocr",
      "br-verification",
      "projection",
      collectionName,
      processId,
      currentPage,
    ],
    queryFn: () =>
      ocrApi.selectProjection({
        processId,
        collectionName,
        pageNumber: currentPage,
        verificationComponent: VERIFICATION_COMPONENT,
        tableName,
      }),
    enabled: requestReady,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // JSON view — fetched lazily via `/export_json` when the toggle is
  // on. Mirrors ocr-ui/.../verification/index.jsx which dispatches
  // `exportJson({process_id})` and renders the returned payload via
  // JsonViewer. The export endpoint produces the combined
  // {Header, ItemsDetails, ShipmentContainerDetails} structure — the
  // per-page projection used by the side tables can't be aggregated
  // into that shape on the client.
  const exportJsonQuery = useQuery({
    queryKey: ["ocr", "br-verification", "export-json", processId],
    queryFn: () => ocrApi.exportJson(processId),
    enabled: requestReady && showJson,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Resolve total pages from a few possible shapes the backend uses:
  //
  //   1. TV cockpit shape — homePayload.pages is a map keyed by page
  //      number; totalPages = max key.
  //   2. BR Final Checker shape — homePayload.content.total_pages is a
  //      plain number (the reference reads this via
  //      `get(response.content, 'total_pages')` in
  //      ocr-ui/.../verification/index.jsx).
  //   3. Wrapped envelope — same fields nested under homePayload.data.
  //   4. Projection fallback — the select_projection response can carry
  //      `total_pages` directly when verification_home doesn't.
  //
  // Without all four, multi-page BR documents render only page 1
  // because the page-nav buttons stay disabled at totalPages === 1.
  // NOTE: Must be declared AFTER projectionQuery so its `.data` is in
  // scope — earlier we hit a TDZ ReferenceError forward-referencing it.
  const totalPages = useMemo<number>(() => {
    const readNumber = (v: unknown): number | null => {
      if (typeof v === "number" && Number.isFinite(v) && v > 0) return v;
      if (typeof v === "string") {
        const n = parseInt(v, 10);
        if (Number.isFinite(n) && n > 0) return n;
      }
      return null;
    };

    // 1. Map of page objects (TV shape).
    if (pages) {
      const keys = Object.keys(pages)
        .map((k) => parseInt(k, 10))
        .filter((n) => !Number.isNaN(n));
      if (keys.length > 0) return Math.max(...keys);
    }

    // 2-3. `total_pages` field anywhere in the home response.
    const home = (homePayload ?? {}) as Record<string, unknown>;
    const homeCandidates: unknown[] = [
      home.total_pages,
      (home.content as Record<string, unknown> | undefined)?.total_pages,
      (home.data as Record<string, unknown> | undefined)?.total_pages,
      (
        (home.data as Record<string, unknown> | undefined)?.content as
          | Record<string, unknown>
          | undefined
      )?.total_pages,
    ];
    for (const c of homeCandidates) {
      const n = readNumber(c);
      if (n) return n;
    }

    // 4. Projection response — sometimes carries the count itself.
    const proj = (projectionQuery.data ?? {}) as Record<string, unknown>;
    const projCandidates: unknown[] = [
      proj.total_pages,
      (proj.data as Record<string, unknown> | undefined)?.total_pages,
    ];
    for (const c of projCandidates) {
      const n = readNumber(c);
      if (n) return n;
    }
    const projKeys = Object.keys(proj)
      .map((k) => parseInt(k, 10))
      .filter((n) => !Number.isNaN(n));
    if (projKeys.length > 0) return Math.max(...projKeys);

    return 1;
  }, [pages, homePayload, projectionQuery.data]);

  // Locate the page-specific block inside the projection response. The
  // reference's BR shape is `{[pageNumber]: {image_path, image_size,
  // extracted_content}}`. We tolerate both that and a flat
  // `{extracted_content}` shape so the page still renders if the
  // backend's envelope changes.
  const projectionPage = useMemo<Record<string, unknown> | null>(() => {
    const data = projectionQuery.data;
    if (!data || typeof data !== "object") return null;
    const obj = data as Record<string, unknown>;
    const direct = obj[String(currentPage)];
    if (direct && typeof direct === "object") {
      return direct as Record<string, unknown>;
    }
    if (obj.data && typeof obj.data === "object") {
      const inner = (obj.data as Record<string, unknown>)[String(currentPage)];
      if (inner && typeof inner === "object") {
        return inner as Record<string, unknown>;
      }
    }
    // Fallback: the response itself is the page-data block.
    return obj;
  }, [projectionQuery.data, currentPage]);

  const tabData = useMemo<unknown>(() => {
    if (!projectionPage) return undefined;
    if (projectionPage.extracted_content !== undefined) {
      return projectionPage.extracted_content;
    }
    return projectionPage;
  }, [projectionPage]);

  // The reference reads `image_path` straight out of the projection
  // response (`response[currentPage].image_path`), not from
  // `verification_home`. We honor that — the BR flow stamps the image
  // path on the projection envelope — and fall back to the
  // verification_home pages map in case a deployment ever returns the
  // older shape.
  const imagePath: string =
    (typeof projectionPage?.image_path === "string"
      ? (projectionPage.image_path as string)
      : "") ||
    (typeof pageData?.image_path === "string"
      ? (pageData.image_path as string)
      : "");
  const imageSize: string =
    (typeof projectionPage?.image_size === "string"
      ? (projectionPage.image_size as string)
      : "") ||
    (typeof pageData?.image_size === "string"
      ? (pageData.image_size as string)
      : "");

  // ----- Mutations -----------------------------------------------------

  const actionContext = {
    workflow_id: workflowId,
    verification_component: VERIFICATION_COMPONENT,
    table_name: tableName,
  };

  const invalidateProjections = () => {
    queryClient.invalidateQueries({
      queryKey: ["ocr", "br-verification"],
      exact: false,
    });
  };

  // Save — mirrors ocr-ui's onSave payload exactly:
  //   { process_id, page_no, collection_name,
  //     verification_component, table_name,
  //     content: JSON.stringify({ image_path, extracted_content }) }
  const saveMutation = useMutation({
    mutationFn: () =>
      ocrApi.verificationSave(processId, {
        ...actionContext,
        page_no: String(currentPage),
        collection_name: collectionName,
        content: JSON.stringify({
          image_path: imagePath,
          extracted_content: tabData ?? {},
        }),
      }),
    onSuccess: () => {
      toast.success("Saved successfully");
      invalidateProjections();
    },
    onError: (err) =>
      toast.error("Save failed. Please try again", {
        description: err instanceof Error ? err.message : String(err),
      }),
  });

  // Reset — `{ process_id, page_no, collection_name,
  //            verification_component, table_name }`
  const resetMutation = useMutation({
    mutationFn: () =>
      ocrApi.verificationReset({
        processId,
        collectionName,
        pageNumber: currentPage,
        verificationComponent: VERIFICATION_COMPONENT,
        tableName,
      }),
    onSuccess: () => {
      toast.success("Reset done successfully");
      invalidateProjections();
    },
    onError: (err) =>
      toast.error("Reset failed. Please try again", {
        description: err instanceof Error ? err.message : String(err),
      }),
  });

  // Clear — `{ process_id, verification_component, collection_name,
  //            page_no, table_name }`
  const clearMutation = useMutation({
    mutationFn: () =>
      ocrApi.verificationClear({
        processId,
        collectionName,
        pageNumber: currentPage,
        verificationComponent: VERIFICATION_COMPONENT,
        tableName,
      }),
    onSuccess: () => {
      toast.success("Clear done successfully");
      invalidateProjections();
    },
    onError: (err) =>
      toast.error("Clear failed. Please try again", {
        description: err instanceof Error ? err.message : String(err),
      }),
  });

  // Reject — `{ process_id, verification_component, table_name }`.
  // Reference's onReject doesn't send a reason; we keep the prompt for
  // UX continuity but only attach it if non-empty.
  const rejectMutation = useMutation({
    mutationFn: (reason: string) =>
      ocrApi.verificationReject(processId, {
        verification_component: VERIFICATION_COMPONENT,
        table_name: tableName,
        ...(reason ? { reason } : {}),
      }),
    onSuccess: () => {
      toast.success("Reject done successfully");
      queryClient.invalidateQueries({ queryKey: ["ocr", "tracking"] });
      navigate(inboxPath);
    },
    onError: (err) =>
      toast.error("Reject failed. Please try again", {
        description: err instanceof Error ? err.message : String(err),
      }),
  });

  // Submit — `{ workflow_id, process_id, verification_component,
  //              table_name }` (actionContext already has all three).
  const submitMutation = useMutation({
    mutationFn: () => ocrApi.verificationSubmit(processId, actionContext),
    onSuccess: () => {
      toast.success("Submit done successfully");
      queryClient.invalidateQueries({ queryKey: ["ocr", "tracking"] });
      navigate(inboxPath);
    },
    onError: (err) =>
      toast.error("Submit failed. Please try again", {
        description: err instanceof Error ? err.message : String(err),
      }),
  });

  // Reset page state when the doc / tab changes.
  useEffect(() => {
    setActiveBoundary(null);
  }, [currentPage, activeTab]);

  const goToPage = (raw: string) => {
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) return;
    const clamped = Math.max(1, Math.min(totalPages, n));
    setCurrentPage(clamped);
    setGoToInput("");
  };

  /**
   * Download the tracking-page export as a local file. Calls
   * `/tracking_page/export` with the current process + workflow IDs
   * and the tracking collection name, then streams the response into a
   * temporary <a> blob anchor. The JSON View toggle still uses the
   * `/export_json` payload — only the download button hits this newer
   * endpoint, since it returns the bulk-export shape.
   */
  const handleDownloadJson = async () => {
    try {
      const raw = await ocrApi.exportTrackingPage({
        processIds: [processId],
        workflowIds: [workflowId],
        tableName,
      });
      const data = prepareExportJson(raw);
      const filename = `${filenameParam || processId || "extracted"}.json`.replace(
        /\.(pdf|png|jpe?g|tiff?)$/i,
        "",
      ) + ".json";
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("JSON downloaded");
    } catch (err) {
      toast.error("Download failed", {
        description: err instanceof Error ? err.message : String(err),
      });
    }
  };

  // ----- Render --------------------------------------------------------

  if (!requestReady) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <p className="text-sm text-gray-500">Loading verification…</p>
      </div>
    );
  }

  const filename =
    filenameParam ||
    (typeof homePayload?.filename === "string"
      ? (homePayload.filename as string)
      : "");

  const anyMutating =
    saveMutation.isPending ||
    resetMutation.isPending ||
    clearMutation.isPending ||
    rejectMutation.isPending ||
    submitMutation.isPending;

  return (
    <div className="flex h-screen flex-col bg-gray-50">
      {/* === Header ====================================================== */}
      <header className="flex flex-shrink-0 items-center justify-between gap-4 border-b border-gray-200 bg-white px-6 py-3">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate(inboxPath)}
            className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Back to tracking"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-base font-semibold text-gray-900">
              Verification
            </h1>
            {filename ? (
              <p className="text-xs text-gray-500">{filename}</p>
            ) : null}
          </div>

          {/* Page navigation */}
          <div className="ml-4 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-30"
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="inline-flex h-7 min-w-7 items-center justify-center rounded bg-blue-600 px-2 text-xs font-semibold text-white">
              {currentPage}
            </span>
            <span className="text-xs text-gray-400 tabular-nums">
              / {totalPages}
            </span>
            <button
              type="button"
              onClick={() =>
                setCurrentPage((p) => Math.min(totalPages, p + 1))
              }
              disabled={currentPage >= totalPages}
              className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-30"
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <span className="ml-2 text-xs text-gray-500">Go to</span>
            <input
              type="text"
              value={goToInput}
              onChange={(e) => setGoToInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") goToPage(goToInput);
              }}
              placeholder="#"
              className="w-12 rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={() => goToPage(goToInput)}
              className="rounded p-1 text-gray-500 hover:bg-gray-100"
              aria-label="Go to page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>

            {/* JSON View — switch-style toggle. Highlight markers
                always render on the document; no separate Image View
                mode. */}
            <button
              type="button"
              role="switch"
              aria-checked={showJson}
              onClick={() => setShowJson((v) => !v)}
              className="ml-3 inline-flex items-center gap-2"
            >
              <span
                className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${
                  showJson ? "bg-blue-600" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    showJson ? "translate-x-4" : "translate-x-0.5"
                  }`}
                />
              </span>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-700">
                <Code2 className="h-3.5 w-3.5" />
                JSON View
              </span>
            </button>

            {/* Download JSON — fetches /export_json (or reuses the
                cached query data) and triggers a browser save. Disabled
                while a fetch is mid-flight to avoid double-clicks
                spawning duplicate requests. */}
            <button
              type="button"
              onClick={handleDownloadJson}
              disabled={exportJsonQuery.isFetching}
              title="Download JSON"
              aria-label="Download JSON"
              className="ml-2 inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
            >
              <Download className="h-3.5 w-3.5" />
              JSON
            </button>
          </div>
        </div>

        {/* Right-side actions */}
        <div className="flex items-center gap-2">
          <ActionButton
            label="Reset"
            tone="neutral"
            disabled={anyMutating}
            onClick={() => {
              if (window.confirm("Reset extracted content for this page?")) {
                resetMutation.mutate();
              }
            }}
            icon={<RotateCcw className="h-3.5 w-3.5" />}
          />
          <ActionButton
            label="Save"
            tone="amber"
            disabled={anyMutating}
            onClick={() => saveMutation.mutate()}
            icon={<Save className="h-3.5 w-3.5" />}
          />
          <ActionButton
            label="Clear"
            tone="neutral"
            disabled={anyMutating}
            onClick={() => {
              if (window.confirm("Clear extracted content for this page?")) {
                clearMutation.mutate();
              }
            }}
            icon={<Eraser className="h-3.5 w-3.5" />}
          />
          <ActionButton
            label="Reject"
            tone="rose"
            disabled={anyMutating}
            onClick={() => {
              const reason = window.prompt(
                "Reason for rejection (optional):",
              ) ?? "";
              if (window.confirm("Reject this document?")) {
                rejectMutation.mutate(reason);
              }
            }}
            icon={<XCircle className="h-3.5 w-3.5" />}
          />
          <ActionButton
            label="Submit"
            tone="emerald"
            disabled={anyMutating}
            onClick={() => {
              if (window.confirm("Submit BR verification?")) {
                submitMutation.mutate();
              }
            }}
            icon={<Send className="h-3.5 w-3.5" />}
          />
        </div>
      </header>

      {/* === Body ========================================================= */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: document image. Vertical scroll only — the image is
            sized to fit the section width (`fitWidth` below), so a
            horizontal scrollbar would only ever appear if the image
            itself had stray overflow. */}
        <section
          ref={docPan.ref}
          {...docPan.panHandlers}
          className={`${
            isFullscreen
              ? "fixed inset-0 z-50 flex min-w-0 flex-col overflow-auto bg-gray-100 p-6"
              : "relative flex flex-1 min-w-0 flex-col overflow-auto bg-gray-100 p-4"
          } ${docPan.isPanning ? "cursor-grabbing select-none" : "cursor-grab"}`}
        >
          {/* Toolbar — zoom controls + fullscreen toggle. Floats over
              the top-right of the document pane. Only shown when there
              is actually an image. Hidden during initial load to keep
              the loading spinner uncluttered. */}
          {imagePath && !homeQuery.isLoading && (
            <div className="pointer-events-none sticky top-0 z-20 flex justify-end">
              <div
                className="pointer-events-auto flex items-center gap-1 rounded-lg border border-gray-200 bg-white/95 px-1.5 py-1 shadow-sm backdrop-blur"
                role="toolbar"
                aria-label="Document toolbar"
              >
                <button
                  type="button"
                  onClick={() => setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP))}
                  disabled={zoom <= ZOOM_MIN}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                  title={`Zoom out (-${ZOOM_STEP}%)`}
                  aria-label="Zoom out"
                >
                  <ZoomOut size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => setZoom(100)}
                  className="inline-flex min-w-[42px] items-center justify-center rounded-md px-1.5 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-100"
                  title="Reset zoom to 100%"
                  aria-label="Reset zoom"
                >
                  {zoom}%
                </button>
                <button
                  type="button"
                  onClick={() => setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP))}
                  disabled={zoom >= ZOOM_MAX}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                  title={`Zoom in (+${ZOOM_STEP}%)`}
                  aria-label="Zoom in"
                >
                  <ZoomIn size={14} />
                </button>
                <div className="mx-1 h-4 w-px bg-gray-200" />
                {/* Fullscreen toggle — expands the document pane to fill
                    the viewport with a fixed-position overlay. Esc also
                    exits. Icon flips to Minimize2 while active. */}
                <button
                  type="button"
                  onClick={() => setIsFullscreen((v) => !v)}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                  title={isFullscreen ? "Exit fullscreen (Esc)" : "Fullscreen"}
                  aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                  aria-pressed={isFullscreen}
                >
                  {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                </button>
              </div>
            </div>
          )}
          {homeQuery.isLoading ? (
            <div className="flex h-full items-center justify-center text-sm text-gray-500">
              Loading document…
            </div>
          ) : !imagePath ? (
            <div className="flex h-full items-center justify-center text-sm text-gray-500">
              No image preview available
            </div>
          ) : (
            <DocumentImageWithHighlight
              src={imagePath}
              alt={filename || "Document"}
              naturalSizeHint={imageSize}
              highlightBoundary={activeBoundary ?? ""}
              zoom={zoom}
              fitWidth
            />
          )}
        </section>

        {/* Right: tabs + data */}
        {/* Right pane shares the body width 50/50 with the document
            image. Both sides use `flex-1` and `min-w-0` so a wide
            line-item table can scroll inside the pane instead of
            blowing out the layout. */}
        <aside className="flex flex-1 min-w-0 flex-col overflow-hidden border-l border-gray-200 bg-white">
          {/* Tab switcher */}
          <div className="flex flex-shrink-0 border-b border-gray-200">
            <TabButton
              label="Line Item"
              active={activeTab === "lineitem"}
              onClick={() => setActiveTab("lineitem")}
            />
            <TabButton
              label="Field Item"
              active={activeTab === "fielditem"}
              onClick={() => setActiveTab("fielditem")}
            />
          </div>

          {/* Tab content — JSON viewer manages its own scroll/padding so
              we don't double-wrap it. Field/line panels keep the outer
              padded scroller.
              JSON view uses the document-wide /export_json payload
              (Header / ItemsDetails / ShipmentContainerDetails), not
              the per-tab projection. */}
          {showJson ? (
            <div className="flex-1 overflow-hidden">
              {exportJsonQuery.isLoading ? (
                <div className="flex h-full items-center justify-center text-xs text-gray-500">
                  Loading JSON…
                </div>
              ) : exportJsonQuery.isError ? (
                <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center text-xs text-rose-600">
                  <span>
                    Couldn't load JSON
                    {exportJsonQuery.error instanceof Error
                      ? `: ${exportJsonQuery.error.message}`
                      : "."}
                  </span>
                  <button
                    type="button"
                    onClick={() => exportJsonQuery.refetch()}
                    className="rounded-md border border-rose-200 bg-white px-2 py-1 text-rose-700 hover:bg-rose-50"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <ResponseJsonViewer
                  value={prepareExportJson(exportJsonQuery.data)}
                />
              )}
            </div>
          ) : (
            <div className="flex-1 overflow-auto p-4">
              {activeTab === "fielditem" ? (
                <FieldItemPanel
                  fields={tabData}
                  activeBoundary={activeBoundary}
                  onSelectField={(sel) => setActiveBoundary(sel.boundary)}
                  isLoading={projectionQuery.isLoading}
                  onRetry={() => projectionQuery.refetch()}
                  hideConfiguration
                />
              ) : (
                <LineItemPanel
                  data={tabData}
                  onSelectCell={(cell) =>
                    setActiveBoundary(cell.boundary || null)
                  }
                  isLoading={projectionQuery.isLoading}
                  onRetry={() => projectionQuery.refetch()}
                  hideConfiguration
                  hideRowSelection
                />
              )}
            </div>
          )}
        </aside>
      </div>

      {/* Lavender connecting line: active field row → bbox on document.
          Mirrors the cockpit's behaviour. The line draws itself off the
          DOM rectangles of `[data-active-field='true']` (set by
          FieldItemPanel / LineItemPanel on the active row) and
          `[data-active-bbox='true']` (set by DocumentImageWithHighlight
          on the highlight rect), so it always lands precisely on the
          bbox edge regardless of zoom or scroll. */}
      {activeBoundary ? (
        <FieldConnectingLine
          sourceSelector="[data-active-field='true']"
          targetSelector="[data-active-bbox='true']"
          changeKey={activeBoundary}
        />
      ) : null}
    </div>
  );
}

// --------------------------------------------------------------- helpers

/**
 * Strip the `{status, data}` envelope the export-json endpoint wraps
 * its payload in, then drop every null / undefined value recursively.
 *
 * Reviewers don't need to see the wrapper or the dozens of unset
 * Header columns — only the fields the pipeline actually populated.
 * Empty strings, `0`, `false`, and empty arrays / objects are KEPT
 * (they're valid extracted values; "0.00 MYR" is a real result, not
 * a missing one).
 */
function prepareExportJson(raw: unknown): unknown {
  if (raw === null || raw === undefined) return {};

  // Unwrap a single layer of `{status, data: {...}}`. Some endpoints
  // also nest under `data.data` — handle that too.
  const unwrap = (v: unknown): unknown => {
    if (!v || typeof v !== "object" || Array.isArray(v)) return v;
    const obj = v as Record<string, unknown>;
    if (
      "data" in obj &&
      obj.data &&
      typeof obj.data === "object" &&
      // Don't unwrap if the response IS legitimately a `data` field
      // (i.e. when there's no `status` sibling pointing at the
      // envelope shape). Keeps us defensive against future endpoints
      // that return a bare object with a `data` key inside.
      ("status" in obj || "message" in obj)
    ) {
      return unwrap(obj.data);
    }
    return v;
  };

  const stripNulls = (v: unknown): unknown => {
    if (v === null || v === undefined) return undefined;
    if (Array.isArray(v)) {
      return v
        .map(stripNulls)
        .filter((item) => item !== undefined);
    }
    if (typeof v === "object") {
      const out: Record<string, unknown> = {};
      for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
        const cleaned = stripNulls(val);
        if (cleaned !== undefined) out[k] = cleaned;
      }
      return out;
    }
    return v;
  };

  const cleaned = stripNulls(unwrap(raw));
  return cleaned ?? {};
}

interface ActionButtonProps {
  label: string;
  tone: "neutral" | "amber" | "rose" | "emerald";
  disabled?: boolean;
  onClick: () => void;
  icon: React.ReactNode;
}

function ActionButton({
  label,
  tone,
  disabled,
  onClick,
  icon,
}: ActionButtonProps) {
  // Outline buttons in different tones so the page mirrors the header
  // ribbon from the screenshot (Reset/Save/Clear/Reject/Submit).
  const tones: Record<ActionButtonProps["tone"], string> = {
    neutral:
      "border-gray-300 bg-white text-gray-700 hover:bg-gray-50",
    amber:
      "border-amber-300 bg-white text-amber-700 hover:bg-amber-50",
    rose: "border-rose-300 bg-white text-rose-700 hover:bg-rose-50",
    emerald:
      "border-emerald-500 bg-emerald-600 text-white hover:bg-emerald-700",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-xs font-semibold shadow-sm disabled:opacity-50 ${tones[tone]}`}
    >
      {icon}
      {label}
    </button>
  );
}

function TabButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 px-4 py-2.5 text-sm font-semibold transition-colors ${
        active
          ? "border-b-2 border-blue-600 bg-blue-50/50 text-blue-700"
          : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
      }`}
    >
      {label}
    </button>
  );
}
